import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useToast } from '../contexts/ToastContext';

interface ProviderTeamAccessListProps { providerId: string | null }

type AccessUser = {
  auth_user_id: string;
  email: string | null;
  name: string | null;
  status: string;
  invited_at: string | null;
  joined_at: string | null;
  receives_order_notifications?: boolean;
  role?: string | null;
};

const ProviderTeamAccessList: React.FC<ProviderTeamAccessListProps> = ({ providerId }) => {
  const toast = useToast();
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState<string>("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  const toggleNotificationRecipient = async (user: AccessUser) => {
    if (!providerId) return;
    const next = !user.receives_order_notifications;
    setTogglingUserId(user.auth_user_id);
    // optimistic update
    setUsers((prev) => prev.map((u) =>
      u.auth_user_id === user.auth_user_id ? { ...u, receives_order_notifications: next } : u
    ));
    const { error } = await supabase.rpc('set_provider_notification_recipient', {
      p_provider_id: providerId,
      p_auth_user_id: user.auth_user_id,
      p_enabled: next,
    });
    setTogglingUserId(null);
    if (error) {
      // revert on failure
      setUsers((prev) => prev.map((u) =>
        u.auth_user_id === user.auth_user_id ? { ...u, receives_order_notifications: !next } : u
      ));
      toast.error(error.message || 'Failed to update notification recipient');
    }
  };

  const load = async () => {
    if (!providerId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("provider-access-list", { body: { provider_id: providerId } });
      if (error) throw error;
      const list = (data as any)?.users as AccessUser[];
      setUsers(Array.isArray(list) ? list : []);
    } catch (e: any) {
      const msg = e?.message || "Failed to load team access";
      if (String(msg).toLowerCase().includes("forbidden")) {
        toast.error("You do not have access to this provider.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [providerId]);

  const handleInviteClick = async () => {
    if (!providerId) return;
    setShowInviteModal(true);
    setInviteLoading(true);
    setInviteLink("");

    try {
      const { data, error } = await supabase.functions.invoke("provider-invite-create", {
        body: { provider_id: providerId, expires_in_hours: 72 }, // 3 days default
      });
      if (error) throw error;
      const token = (data as any)?.token as string;
      if (!token) throw new Error("Failed to generate invite token");
      const url = `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}&type=provider`;
      setInviteLink(url);
    } catch (e: any) {
      toast.error(e?.message || "Failed to create invite link");
    } finally {
      setInviteLoading(false);
    }
  };

  const fmt = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  return (
    <div>
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowInviteModal(false)} />
          <div className="relative modal-body rounded-2xl w-full max-w-lg mx-4">
            <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
              <h3 className="text-lg font-semibold  text-white">Invite Team Member</h3>
              <button onClick={() => setShowInviteModal(false)} className="text-white/35 hover:text-white/70  text-sm">Close</button>
            </div>
            <div className="p-4">
              <p className="text-sm text-white/50  mb-3">Share this link to grant access to your provider account. It expires in 3 days.</p>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  readOnly
                  value={inviteLoading ? "Generating..." : (inviteLink || "")}
                  className="flex-1 px-3 py-2 bg-transparent border border-white/15 text-white  text-xs"
                />
                <button
                  disabled={inviteLoading || !inviteLink}
                  onClick={() => inviteLink && navigator.clipboard.writeText(inviteLink)}
                  className="px-3 py-2 border border-white/15  hover:bg-white/[0.08]  text-sm disabled:opacity-50"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="p-3 border-t border-white/[0.08] flex justify-end">
              <button onClick={() => setShowInviteModal(false)} className="px-4 py-2 bg-orange-500 text-white border-2 border-orange-500 hover:bg-orange-400 rounded-xl transition-colors text-sm">Done</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h4 className="text-base font-medium text-orange-400 ">Team Access</h4>
        <div className="flex gap-2">
          <button onClick={handleInviteClick} disabled={!providerId} className="px-3 py-1.5 border border-white/15 bg-orange-500 text-white rounded-xl text-xs hover:bg-orange-400 transition-colors disabled:opacity-50">Invite User</button>
          <button onClick={load} disabled={loading || !providerId} className="px-3 py-1.5 border border-white/15  text-white  text-xs hover:bg-white/[0.08] disabled:opacity-50">Refresh</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-6 gap-2 py-2 text-xs  text-white/35">
            <div className="col-span-2 text-left">User</div>
            <div className="col-span-1 text-left">Status</div>
            <div className="col-span-1 text-left">Invited</div>
            <div className="col-span-1 text-left">Joined</div>
            <div className="col-span-1 text-left" title="Receive new-order email notifications">Email Notifications</div>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-white/[0.04]">
            {(!users || users.length === 0) && !loading ? (
              <div className="py-4 text-center text-white/35  text-sm">No team members</div>
            ) : (
              users.map((u) => {
                const label = u.email || "—";
                return (
                  <div key={u.auth_user_id} className="grid grid-cols-6 gap-2 items-center py-2">
                    <div className="col-span-2 text-left">
                      <div className="text-white  text-sm truncate">{label}</div>
                      <div className="text-white/35  text-xs truncate">{u.name || ""}</div>
                    </div>
                    <div className="col-span-1 text-left text-sm ">{u.status}</div>
                    <div className="col-span-1 text-left text-xs ">{fmt(u.invited_at)}</div>
                    <div className="col-span-1 text-left text-xs ">{fmt(u.joined_at)}</div>
                    <div className="col-span-1 text-left">
                      <input
                        type="checkbox"
                        checked={!!u.receives_order_notifications}
                        onChange={() => toggleNotificationRecipient(u)}
                        disabled={togglingUserId === u.auth_user_id}
                        className="w-4 h-4 cursor-pointer accent-orange-500 disabled:opacity-50"
                        title="Send new-order emails to this user"
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderTeamAccessList;
